/**
 * Sub-dialog for picking objects to exclude from a blueprint. Opened from the
 * Database Blueprint Setup dialog's "Set exclusions…" button.
 *
 * Two-list (shuttle) UX:
 *   • LEFT  — available objects, filterable by kind and free-text search.
 *   • RIGHT — currently-excluded objects.
 * Buttons move single rows or bulk-move-filtered between the two sides.
 *
 * The picker takes the current `excludedKeys` from the parent and reports back
 * the full new set on every change (via `onChange`) so the parent's submit
 * round-trip stays unchanged.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { exclusionRowKey } from '../utils/exclusionKey';

type ExclusionKind = 'table' | 'view' | 'procedure' | 'function';

type ExclusionRow = {
  kind: ExclusionKind;
  schema: string;
  name: string;
};

const KIND_ORDER: ReadonlyArray<ExclusionKind> = [
  'table',
  'view',
  'procedure',
  'function',
];

const KIND_LABEL: Record<ExclusionKind, string> = {
  table: 'Tables',
  view: 'Views',
  procedure: 'Procedures',
  function: 'Functions',
};

// P2 — the space-joined `${kind} ${schema} ${name}` shape collides on
// legal SQL identifiers that contain spaces. Route through the shared
// NUL-delimited helper so picker set membership is collision-free.
const rowKey = (r: ExclusionRow): string => exclusionRowKey(r);

type Props = {
  open: boolean;
  onClose: () => void;
  /** All candidate objects to show (already-sorted upstream). */
  rows: ReadonlyArray<ExclusionRow>;
  /** Set of keys currently ticked as excluded. */
  excludedKeys: ReadonlySet<string>;
  /** Receives the new excluded-keys set whenever it changes. */
  onChange: (next: Set<string>) => void;
};

export function ExclusionPickerDialog({
  open,
  onClose,
  rows,
  excludedKeys,
  onChange,
}: Props) {
  // Search applies to LEFT list only — the right (Excluded) list always shows
  // every currently-excluded object regardless of search query, so users can
  // see and re-include what they've excluded even after filtering Available.
  const [search, setSearch] = useState('');
  // Kind filter also applies to LEFT list only — the right list shows
  // everything the user has excluded regardless of current kind filter.
  const availableKinds = useMemo(() => {
    const present = new Set<ExclusionKind>();
    for (const r of rows) present.add(r.kind);
    return KIND_ORDER.filter((k) => present.has(k));
  }, [rows]);

  const [enabledKinds, setEnabledKinds] = useState<Set<ExclusionKind>>(
    () => new Set(availableKinds),
  );
  // Reset kind filter to "all on" when the candidate set changes (mostly
  // matters when the dialog re-opens with fresh data). useEffect (not
  // useMemo) because this is a side effect — useMemo's callback execution
  // is not guaranteed by React, and calling setState during render is not
  // a render-phase computation. `availableKinds` is itself memoized on
  // [rows] above, so its reference is stable until rows actually change.
  useEffect(() => {
    if (open) setEnabledKinds(new Set(availableKinds));
  }, [open, availableKinds]);

  const toggleKind = (kind: ExclusionKind) => {
    setEnabledKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const q = search.trim().toLowerCase();
  const matchesQuery = useCallback(
    (r: ExclusionRow): boolean => {
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.schema.toLowerCase().includes(q) ||
        r.kind.toLowerCase().includes(q)
      );
    },
    [q],
  );

  // Selection model for each side — clicking a row toggles its selection in
  // its own pane. Move buttons act on the selected set; bulk buttons act on
  // the filtered set regardless of selection.
  const [leftSelected, setLeftSelected] = useState<Set<string>>(new Set());
  const [rightSelected, setRightSelected] = useState<Set<string>>(new Set());

  const availableRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          !excludedKeys.has(rowKey(r)) &&
          (enabledKinds.size === availableKinds.length || enabledKinds.has(r.kind)) &&
          matchesQuery(r),
      ),
    [rows, excludedKeys, enabledKinds, availableKinds.length, matchesQuery],
  );

  // The Excluded pane intentionally ignores `search` and `enabledKinds`: it
  // must always show every currently-excluded object so users can re-include
  // anything they previously excluded, no matter what filter they typed.
  const excludedRows = useMemo(
    () => rows.filter((r) => excludedKeys.has(rowKey(r))),
    [rows, excludedKeys],
  );

  const moveToExcluded = (keys: ReadonlySet<string> | ReadonlyArray<string>) => {
    const next = new Set(excludedKeys);
    for (const k of keys) next.add(k);
    onChange(next);
    setLeftSelected(new Set());
  };

  const moveToAvailable = (keys: ReadonlySet<string> | ReadonlyArray<string>) => {
    const next = new Set(excludedKeys);
    for (const k of keys) next.delete(k);
    onChange(next);
    setRightSelected(new Set());
  };

  const handleExcludeSelected = () => {
    moveToExcluded(leftSelected);
  };
  const handleExcludeAllFiltered = () => {
    moveToExcluded(availableRows.map(rowKey));
  };
  const handleIncludeSelected = () => {
    moveToAvailable(rightSelected);
  };
  const handleClearAll = () => {
    moveToAvailable(excludedRows.map(rowKey));
  };

  const toggleLeftSelection = (r: ExclusionRow) => {
    const k = rowKey(r);
    setLeftSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleRightSelection = (r: ExclusionRow) => {
    const k = rowKey(r);
    setRightSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl w-[min(64rem,calc(100vw-2rem))]"
        data-testid="database-blueprint-exclusion-picker-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Exclude objects from blueprint</DialogTitle>
          <DialogDescription>
            Objects on the right are skipped on every Refresh from DB and
            don&rsquo;t appear in preview / three-way diffs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {/* Search + kind chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name, schema, or kind"
                className="pl-8 h-8 text-sm"
                data-testid="database-blueprint-exclusion-picker-search"
              />
            </div>
            {availableKinds.length > 1 && (
              <div
                className="flex flex-wrap items-center gap-1.5"
                data-testid="database-blueprint-exclusion-picker-kinds"
              >
                {availableKinds.map((kind) => {
                  const active = enabledKinds.has(kind);
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => toggleKind(kind)}
                      aria-pressed={active}
                      className={cn(
                        'text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm border transition-colors',
                        active
                          ? 'bg-primary/10 border-primary/40 text-foreground'
                          : 'bg-transparent border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/50',
                      )}
                      data-testid={`database-blueprint-exclusion-picker-kind-${kind}`}
                    >
                      {KIND_LABEL[kind]}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Two-list shuttle */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-stretch">
            <ListPane
              title="Available"
              testId="database-blueprint-exclusion-picker-available"
              rows={availableRows}
              selected={leftSelected}
              onToggle={toggleLeftSelection}
              emptyText={
                rows.length === 0
                  ? 'No objects available yet.'
                  : 'No matches for the current filter.'
              }
              countSuffix={`/ ${rows.filter((r) => !excludedKeys.has(rowKey(r))).length}`}
            />

            <div className="flex flex-col items-center justify-center gap-1.5 px-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleExcludeSelected}
                disabled={leftSelected.size === 0}
                title="Exclude selected"
                data-testid="database-blueprint-exclusion-picker-exclude-selected"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleExcludeAllFiltered}
                disabled={availableRows.length === 0}
                title="Exclude all filtered"
                data-testid="database-blueprint-exclusion-picker-exclude-all"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
              <div className="h-1" />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleIncludeSelected}
                disabled={rightSelected.size === 0}
                title="Re-include selected"
                data-testid="database-blueprint-exclusion-picker-include-selected"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleClearAll}
                disabled={excludedRows.length === 0}
                title="Clear all exclusions"
                data-testid="database-blueprint-exclusion-picker-clear-all"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
            </div>

            <ListPane
              title="Excluded"
              titleAccent="destructive"
              testId="database-blueprint-exclusion-picker-excluded"
              rows={excludedRows}
              selected={rightSelected}
              onToggle={toggleRightSelection}
              emptyText="Nothing excluded — every object is included in the blueprint."
              countSuffix={`/ ${excludedKeys.size}`}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            onClick={onClose}
            data-testid="database-blueprint-exclusion-picker-done"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListPane({
  title,
  titleAccent,
  rows,
  selected,
  onToggle,
  emptyText,
  countSuffix,
  testId,
}: {
  title: string;
  titleAccent?: 'destructive';
  rows: ReadonlyArray<ExclusionRow>;
  selected: ReadonlySet<string>;
  onToggle: (row: ExclusionRow) => void;
  emptyText: string;
  countSuffix?: string;
  testId: string;
}) {
  return (
    <div
      className="flex flex-col min-h-0 border rounded-sm overflow-hidden"
      data-testid={testId}
    >
      <div className="flex items-center justify-between px-2 py-1 border-b bg-muted/30 text-xs">
        <span
          className={cn(
            'font-semibold uppercase tracking-wide',
            titleAccent === 'destructive' && 'text-destructive',
          )}
        >
          {title}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {rows.length}
          {countSuffix ? ` ${countSuffix}` : ''}
        </span>
      </div>
      <ScrollArea className="h-[320px]">
        {rows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-3 px-2">{emptyText}</div>
        ) : (
          <ul className="space-y-0.5 p-1">
            {rows.map((row) => {
              const key = rowKey(row);
              const isSelected = selected.has(key);
              const testKey = `${row.kind}-${row.schema}-${row.name}`;
              return (
                <li
                  key={key}
                  onClick={() => onToggle(row)}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1 rounded-sm cursor-pointer min-w-0',
                    isSelected
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent/50',
                  )}
                  data-testid={`database-blueprint-exclusion-picker-row-${testKey}`}
                  data-selected={isSelected ? 'true' : undefined}
                >
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground min-w-[60px] shrink-0">
                    {row.kind}
                  </span>
                  <span
                    className="text-xs font-medium truncate min-w-0 flex-1"
                    title={row.schema ? `${row.schema}.${row.name}` : row.name}
                  >
                    {row.schema ? `${row.schema}.` : ''}
                    {row.name}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
}

